<template>
  <DialogWrapper :dialog="isActive"
                 :persistent="true"
                 overlayColor="#3776CB"
                 opacity=1
                 :maxWidth="900"
                 @close-dialog="closeHandler">
    <template #header>
      <span class="text-h5">
        {{ editMode ? $t("project.dialog.edit.title"): $t("project.dialog.create.title")}}
      </span>
    </template>

    <template #header-actions>
      <TitleVideoButton class="project-video" :outlined="false" videoKey="project.project.video" />
    </template>

    <ProjectTextField data-cy="projectNameField"
                      data-test="name"
                      inputName="name"
                      :value="name"
                      :validatorTrigger="validatorTrigger"
                      @setInput="(value) => { name = value}"/>

    <ProjectTextField data-test="site"
                      inputName="reference"
                      :value="siteTypeRef"
                      :validatorTrigger="validatorTrigger"
                      @setInput="(value) => { siteTypeRef = value}"/>

    <ProjectSelectField data-test="country"
                        data-cy="countryField"
                        :options="formattedCountriesOptions"
                        :value='country3Ref'
                        inputName="country3Ref"
                        :validatorTrigger="validatorTrigger"
                        :project-rules="projectRules"
                        :disabled="displayRenProject"
                        @setInput="setCountry"/>

    <QuestionCards data-test="typeRef"
                   data-cy="projectType"
                   class="mb-6"
                   :input="selectedTypeRef"
                   input-path="project.typeRef"
                   images-folder="/img/project-type"
                   :rule="projectRules.getNestedRule('typeRef')"
                   :error-message="typeRefError"
                   :disabledCard="disableProjectType"
                   @setInput="setTypeRef"/>

    <ProjectTextField data-test="gasStationCode"
                      v-if="displayGasStationCode"
                      inputName="gasStationCode"
                      :value="gasStationCode"
                      :validatorTrigger="validatorTrigger"
                      @setInput="(value) => { gasStationCode = value}"/>

    <ProjectSelectField  v-if="displayRenProject"
                        :options="renProjectOptions"
                        :value='selectedRenProject'
                        inputName="renProject"
                        :validatorTrigger="validatorTrigger"
                        :project-rules="projectRules"
                        @setInput="setRenProject"/>

    <div class="d-flex justify-space-between minimum-space-between" v-if="!displayRenProject">
      <ProjectDateFields data-test="startDate"
                         class="same-size"
                         :value="startDate"
                         :rule="projectRules.getNestedRule('startDate')"
                         inputName="startDate"
                         :project="project"
                         @setInput="value => startDate = value"
                         :validatorTrigger="validatorTrigger"/>

      <ProjectDateFields data-test="activityStartDate"
                         class="same-size"
                         :value="activityStartDate"
                         :rule="projectRules.getNestedRule('activityStartDate')"
                         inputName="activityStartDate"
                         :project="project"
                         @setInput="value => activityStartDate = value"
                         :validatorTrigger="validatorTrigger"/>
    </div>

    <template #actions>
      <div class="d-flex justify-center">
        <ActionButton data-test="save-project"
                      :label="editMode? $t('project.dialog.edit.button'): $t('project.dialog.create.button')"
                      @click="saveProject"
                      data-cy="saveProjectButton"/>
      </div>
    </template>
  </DialogWrapper>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import ErrorsMixin from '@/components/Mixins/ErrorsMixin.vue';
import { ProjectType, ProjectTypeRef } from '@/dto/scenario/ProjectReference';
import _string from '@/helpers/stringHelper';
import { ProjectMeta } from '@/classes/ProjectMeta';
import TitleVideoButton from '@/components/TitleVideoButton.vue';
import { SelectOption } from '@/classes/SelectOption';
import { CountryMeta } from '@/classes/CountryMeta';
import DialogWrapper from '@/components/DialogWrapper.vue';
import ProjectTextField from '@/components/Projects/ProjectTextField.vue';
import ProjectSelectField from '@/components/Projects/ProjectSelectField.vue';
import ProjectDateFields from '@/components/Projects/ProjectDateFields.vue';
import { collator } from '@/helpers/localeHelper';
import QuestionCards from '@/components/Questions/QuestionCards.vue';
import { projectRules } from '@/helpers/rulesModules/buildProjectRules';
import ActionButton from '@/components/ActionButton.vue';
import { InternationalizedLabel } from '@/interfaces/InternationalizedLabel';
import { localeDateFromUTC, localeDateToUTC } from '@/helpers/timeHelper';

@Component({
  components: {
    ActionButton,
    QuestionCards,
    TitleVideoButton,
    DialogWrapper,
    ProjectTextField,
    ProjectSelectField,
    ProjectDateFields
  }
})
export default class SetProjectDialog extends Mixins(ErrorsMixin) {
  @Prop({ type: Boolean, required: true })
  public readonly isActive!: boolean;

  @Prop({ type: Boolean, required: true })
  public readonly editMode!: boolean;

  @Prop({ type: ProjectMeta, required: true })
  public readonly projectToEdit!: ProjectMeta;

  public id?: string;
  public typeRef: ProjectTypeRef | ProjectType | null = null;
  public typeRefError: InternationalizedLabel | null = null;

  public startDate: Date = new Date(new Date().getFullYear(), 0, 1);
  public activityStartDate: Date = new Date(new Date().getFullYear() + 1, 0, 1);

  public name?: string | null = null;
  public country3Ref?: string | null = null;
  public siteTypeRef?: string | null = null;
  public gasStationCode?: string | null = null;

  private renProject?: string | null = null;

  private validatorTrigger = {};

  private projectRules = projectRules;

  // COMPUTED =======================================================
  get project() {
    return new ProjectMeta({
      ...this.projectToEdit,
      name: this.name,
      typeRef: this.typeRef,
      country3Ref: this.country3Ref,
      site: this.siteTypeRef,
      gasStationCode: this.gasStationCode,
      startDate: localeDateToUTC(this.startDate),
      activityStartDate: localeDateToUTC(this.activityStartDate)
    });
  }

  get disableProjectType() {
    const userGroup:Array<string> = this.$store.getters['user/getUserGroups'];
    const disableGroup: any = {
      gas_station_project: true,
      i_c_distributed_generation_project: true,
      utility_scale_pv_plant_project: true,
      wind_onshore_project: true,
      ren_fr_project: true
    };

    for (const group of userGroup) {
      disableGroup[group] = false;
    }

    return disableGroup;
  }

  get displayGasStationCode() {
    return this.typeRef === ProjectTypeRef.gas_station_project;
  }

  // Countries getters
  get getCountriesMap(): Map<string, CountryMeta> {
    return this.$store.getters['structure/getCountriesMap'];
  }

  get formattedCountriesOptions(): SelectOption[] {
    const countriesOption: SelectOption[] = [];
    const arr = Array.from(this.getCountriesMap.values()) as any;
    for (const country of arr) {
      countriesOption.push(
        new SelectOption(country[this.$i18n.locale], country.alpha3)
      );
    }
    countriesOption.sort(this.countrySorter);
    return countriesOption;
  }

  get hasErrors(): boolean {
    return this.errorsLength !== 0 || !(
      this.name &&
      this.country3Ref &&
      this.typeRef &&
      (this.displayGasStationCode ? this.gasStationCode : true) &&
      (this.displayRenProject ? this.renProject : true)
    );
  }

  get renProjectOptions():SelectOption[] {
    const renProjectArr = ['PV', 'WIND'];
    const renProjectList: SelectOption[] = renProjectArr.map((opt) => {
      return { key: opt, label: opt, value: opt };
    });

    return renProjectList;
  }

  get displayRenProject() {
    return this.typeRef && [ProjectType.ren_fr_project, ProjectTypeRef.ren_fr_pv_project, ProjectTypeRef.ren_fr_wind_project].includes(this.typeRef);
  }

  get selectedTypeRef() {
    if ([ProjectTypeRef.ren_fr_pv_project, ProjectTypeRef.ren_fr_wind_project].includes(this.typeRef as ProjectTypeRef)) {
      return ProjectType.ren_fr_project;
    }
    return this.typeRef;
  }

  get selectedRenProject() {
    return this.typeRef === ProjectTypeRef.ren_fr_pv_project ? 'PV' : this.typeRef === ProjectTypeRef.ren_fr_wind_project ? 'WIND' : '';
  }

  // METHODS =======================================================
  protected triggerValidation() {
    this.typeRefError = this.typeRef ? null : { key: 'field.required', params: { fieldName: 'typeRef' } };
    this.validatorTrigger = {}; // TODO get rid of this ugly mechanic
  }

  public setTypeRef(value: ProjectTypeRef| ProjectType): void {
    this.typeRef = value;
    this.triggerValidation();

    if ([ProjectType.ren_fr_project, ProjectTypeRef.ren_fr_pv_project, ProjectTypeRef.ren_fr_wind_project].includes(this.typeRef)) {
      this.setCountry('FRA');
      this.startDate = new Date();
    }
  }

  public setRenProject(value: string): void {
    this.renProject = value;
    value === 'PV' ? this.setTypeRef(ProjectTypeRef.ren_fr_pv_project) : this.setTypeRef(ProjectTypeRef.ren_fr_wind_project);
  }

  private setCountry(value: string): void {
    this.country3Ref = value;
  }

  private getCountryFromCountry3Ref(
    country3Ref: string | null | undefined
  ): CountryMeta | null {
    if (typeof country3Ref !== 'string') {
      return null;
    }
    return (
      Array.from(this.getCountriesMap.values()).find(
        (c) => c.alpha3 === country3Ref
      ) || null
    );
  }

  // Inject project into form fields
  private injectProject(project: ProjectMeta) {
    this.id = project.uuid;
    this.name = project.name;
    const referencedCountry = this.getCountryFromCountry3Ref(
      project.country3Ref
    );
    this.country3Ref = referencedCountry && referencedCountry.alpha3;
    // we set the typeRef after a check on the ProjectTypeRef enum
    if (project.typeRef) {
      this.typeRef = ProjectTypeRef[project.typeRef];
    }
    this.startDate = project.startDate
      ? localeDateFromUTC(project.startDate.substring(0, 10))
      : this.startDate;
    this.activityStartDate = project.activityStartDate
      ? localeDateFromUTC(project.activityStartDate.substring(0, 10))
      : this.activityStartDate;
    this.gasStationCode = project.gasStationCode;
    this.siteTypeRef = project.site;
  }

  private closeHandler() {
    this.$emit('close-dialog');
  }

  private saveProject() { // TODO move this function inside the store
    this.triggerValidation();
    if (!this.hasErrors) {
      const referencedCountry = this.getCountryFromCountry3Ref(
        this.country3Ref
      );
      const payload = {
        name: _string.sanitize(this.name!),
        typeRef: this.typeRef && _string.sanitize(this.typeRef),
        site: this.siteTypeRef ? _string.sanitize(this.siteTypeRef) : '',
        startDate: localeDateToUTC(this.startDate).toISOString().substring(0, 10),
        activityStartDate: localeDateToUTC(this.activityStartDate).toISOString().substring(0, 10),
        country3Ref: this.country3Ref,
        country: referencedCountry && referencedCountry.code,
        gasStationCode:
          this.gasStationCode && _string.sanitize(this.gasStationCode)
      };
      if (this.id) {
        this.$store.dispatch('structure/updateProject', {
          project: payload,
          id: this.id
        });
      } else {
        this.$store.dispatch('structure/createProject', payload);
      }
      this.closeHandler();
    }
  }

  private countrySorter(a: SelectOption, b: SelectOption) {
    return collator.compare(a.label, b.label);
  }
  // HOOKS =======================================================
  @Watch('projectToEdit', { immediate: true })
  private handleProjectEdition(value: ProjectMeta | undefined) {
    if (value) {
      // if we are editing a project, then
      if (this.editMode) {
        this.injectProject(value);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.minimum-space-between {
  column-gap: 1rem;
}
.same-size {
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;
}
</style>
